import { useEffect, useState } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import {
  AuthError as MsalAuthError,
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import AuthError from "./components/auth/AuthError";
import Loading from "./components/auth/Loading";
import LoginScreen from "./components/auth/LoginScreen";
import AppShell from "./components/layout/AppShell";
import BenefitsPage from "./pages/BenefitsPage";
import SessionExpired from "./services/SessionExpired";

/** Eventos de MSAL que dejan al usuario sin poder entrar */
const FAILURE_EVENTS: EventType[] = [
  EventType.LOGIN_FAILURE,
  EventType.ACQUIRE_TOKEN_FAILURE,
  EventType.SSO_SILENT_FAILURE,
];

export default function App() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated: boolean = useIsAuthenticated();
  const [authError, setAuthError] = useState<MsalAuthError | null>(null);

  // sin esto un login fallido (cuenta fuera del tenant, consent denegado) deja
  // al usuario de vuelta en el boton "Ingresar" sin ninguna explicacion
  useEffect(() => {
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (FAILURE_EVENTS.includes(event.eventType)) {
        setAuthError((event.error as MsalAuthError) ?? null);
        return;
      }

      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setAuthError(null);
      }
    });

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  // el correo corporativo viene en `username` de la cuenta activa
  const user: string | undefined = accounts[0]?.username;

  if (authError && !isAuthenticated) {
    return <AuthError error={authError} />;
  }

  // mientras MSAL procesa el redirect no se muestra ni el login ni la app, para
  // no hacer parpadear el boton "Ingresar" en medio de la autenticacion
  if (inProgress !== InteractionStatus.None && !isAuthenticated) {
    return <Loading />;
  }

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <AppShell user={user}>
          <BenefitsPage user={user} />
        </AppShell>

        <SessionExpired />
      </AuthenticatedTemplate>
    </>
  );
}
